
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Target, DollarSign, BarChart3, AlertTriangle } from 'lucide-react';

interface BotMergeAnalysisStatsProps {
  statistics: {
    totalTrades: number;
    winningTrades: number;
    losingTrades: number;
    winRate: number;
    totalPnl: number;
    avgWin: number;
    avgLoss: number;
    profitFactor: number;
    maxDrawdown: number;
    maxDrawdownPercent: number;
    riskRewardRatio: number;
  };
}

export const BotMergeAnalysisStats: React.FC<BotMergeAnalysisStatsProps> = ({
  statistics,
}) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  const mainStats = [
    {
      label: 'Total P&L',
      value: formatCurrency(statistics.totalPnl),
      icon: DollarSign,
      color: statistics.totalPnl >= 0 ? 'text-green-600' : 'text-red-600',
    },
    {
      label: 'Win Rate',
      value: formatPercentage(statistics.winRate),
      icon: Target,
      color: statistics.winRate >= 50 ? 'text-green-600' : 'text-red-600',
    },
    {
      label: 'Total Trades',
      value: statistics.totalTrades.toString(),
      icon: BarChart3,
      color: 'text-blue-600',
    },
    {
      label: 'Risk/Reward',
      value: statistics.riskRewardRatio.toFixed(2),
      icon: TrendingUp,
      color: statistics.riskRewardRatio >= 1 ? 'text-green-600' : 'text-red-600',
    },
  ];

  const performanceStats = [
    {
      label: 'Profit Factor',
      value: statistics.profitFactor.toFixed(2),
      icon: TrendingUp,
      color: statistics.profitFactor >= 1 ? 'text-green-600' : 'text-red-600',
    },
    {
      label: 'Max Drawdown',
      value: formatCurrency(statistics.maxDrawdown),
      icon: AlertTriangle,
      color: 'text-red-600',
    },
    {
      label: 'Max DD %',
      value: formatPercentage(statistics.maxDrawdownPercent),
      icon: TrendingDown,
      color: 'text-red-600',
    },
    {
      label: 'Avg Win',
      value: formatCurrency(statistics.avgWin),
      icon: TrendingUp,
      color: 'text-green-600',
    },
  ];

  return (
    <div className="space-y-4">
      {/* Main Statistics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {mainStats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                    <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                  </div>
                  <Icon className={`h-8 w-8 ${stat.color}`} />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      {/* Performance Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {performanceStats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                    <p className={`text-xl font-bold ${stat.color}`}>{stat.value}</p>
                  </div>
                  <Icon className={`h-6 w-6 ${stat.color}`} />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Detailed Statistics */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Detailed Trade Statistics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Winning Trades:</span>
              <span className="font-medium text-green-600">{statistics.winningTrades}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Losing Trades:</span>
              <span className="font-medium text-red-600">{statistics.losingTrades}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Avg Loss:</span>
              <span className="font-medium text-red-600">{formatCurrency(statistics.avgLoss)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">R:R Ratio:</span>
              <span className={`font-medium ${statistics.riskRewardRatio >= 1 ? 'text-green-600' : 'text-red-600'}`}>
                1:{statistics.riskRewardRatio.toFixed(2)}
              </span>
            </div>
          </div>
          
          {/* Performance Summary */}
          <div className="mt-4 p-3 bg-muted/20 rounded-lg">
            <h4 className="font-medium mb-2">Performance Summary</h4>
            <div className="text-sm space-y-1">
              <div className="flex justify-between">
                <span>Expected Value per Trade:</span>
                <span className={statistics.totalPnl / statistics.totalTrades >= 0 ? 'text-green-600' : 'text-red-600'}>
                  {formatCurrency(statistics.totalPnl / statistics.totalTrades)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Recovery Factor:</span>
                <span className="text-muted-foreground">
                  {statistics.maxDrawdown > 0 ? (statistics.totalPnl / statistics.maxDrawdown).toFixed(2) : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Sharpe-like Ratio:</span>
                <span className="text-muted-foreground">
                  {statistics.avgLoss > 0 ? (statistics.totalPnl / (statistics.maxDrawdown || 1)).toFixed(2) : 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
